/**
 * Global UI, loaded on every page from snippets/pg-scripts.liquid:
 *
 * - Dialog plumbing: any `[data-pg-open="<dialog id>"]` opens that <dialog> as a modal,
 *   `[data-pg-close]` inside it closes it, clicks on the backdrop close it, and focus returns
 *   to the opener. Links keep working without JS (e.g. the cart bowl falls back to /cart).
 * - <pg-header>: sticky header that hides on scroll down and reveals on scroll up, the mega
 *   menu (hover intent, click, Escape, focus leaving), and --pg-header-height.
 * - Add-to-cart feedback: a kibble drops into the header bowl and a toast names the product.
 */
import { PgElement, define, toast, t } from '@pg/core';

// ── Dialogs ──────────────────────────────────────────────────────────────────

/** @type {WeakMap<HTMLDialogElement, Element|null>} */
const openers = new WeakMap();

export function openDialog(id, opener = document.activeElement) {
  const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById(id));
  if (!dialog || dialog.open) return dialog;
  openers.set(dialog, opener);
  dialog.showModal();
  document.documentElement.classList.add('pg-scroll-locked');
  dialog.dispatchEvent(new CustomEvent('pg:dialog:open', { bubbles: true }));
  return dialog;
}

export function closeDialog(dialog) {
  if (!dialog?.open) return;
  dialog.close();
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);
  const opener = target.closest?.('[data-pg-open]');
  if (opener) {
    const id = opener.getAttribute('data-pg-open');
    if (id && document.getElementById(id)) {
      event.preventDefault();
      openDialog(id, opener);
    }
    return;
  }
  const closer = target.closest?.('[data-pg-close]');
  if (closer) {
    closeDialog(closer.closest('dialog'));
    return;
  }
  // A click on the backdrop lands on the <dialog> element itself.
  if (target instanceof HTMLDialogElement && target.open) {
    const rect = target.getBoundingClientRect();
    const { clientX: x, clientY: y } = /** @type {MouseEvent} */ (event);
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) closeDialog(target);
  }
});

document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('pg-scroll-locked');
    const opener = openers.get(dialog);
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
  },
  true,
);

// ── Header ───────────────────────────────────────────────────────────────────

const desktop = matchMedia('(width >= 1200px)');
const canHover = matchMedia('(hover: hover)');

class PgHeader extends PgElement {
  #lastY = 0;
  /** @type {number|undefined} */
  #intent;

  mount() {
    this.#measure();
    new ResizeObserver(() => this.#measure()).observe(this);

    addEventListener('scroll', () => this.#onScroll(), { passive: true, signal: this.signal });

    for (const trigger of this.refs.triggers || []) {
      trigger.addEventListener('click', () => this.#toggle(trigger), { signal: this.signal });
      const item = trigger.closest('.pg-header__nav-item');
      item.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse' || !desktop.matches || !canHover.matches) return;
          clearTimeout(this.#intent);
          // Hover intent: a short delay so sweeping across the nav doesn't flash panels.
          this.#intent = window.setTimeout(() => this.#open(trigger), this.#openTrigger() ? 0 : 120);
        },
        { signal: this.signal },
      );
      item.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse' || !desktop.matches) return;
          clearTimeout(this.#intent);
          this.#intent = window.setTimeout(() => this.#close(trigger), 180);
        },
        { signal: this.signal },
      );
    }

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const open = this.#openTrigger();
        if (open) {
          this.#close(open);
          open.focus();
        }
      },
      { signal: this.signal },
    );

    this.addEventListener(
      'focusout',
      (event) => {
        const next = /** @type {Node|null} */ (event.relatedTarget);
        const open = this.#openTrigger();
        if (open && next && !open.closest('.pg-header__nav-item')?.contains(next)) this.#close(open);
      },
      { signal: this.signal },
    );

    document.addEventListener(
      'click',
      (event) => {
        const open = this.#openTrigger();
        if (open && !open.closest('.pg-header__nav-item')?.contains(/** @type {Node} */ (event.target))) this.#close(open);
      },
      { signal: this.signal },
    );
  }

  #measure() {
    document.documentElement.style.setProperty('--pg-header-height', `${Math.round(this.offsetHeight)}px`);
  }

  #onScroll() {
    const y = Math.max(0, scrollY);
    const delta = y - this.#lastY;
    this.classList.toggle('is-scrolled', y > 4);
    if (this.#openTrigger() || document.querySelector('dialog[open]')) {
      this.#lastY = y;
      return;
    }
    // Hide only after scrolling past the header, and only on a deliberate move.
    if (delta > 6 && y > this.offsetHeight * 2) this.classList.add('is-hidden');
    else if (delta < -6 || y < this.offsetHeight) this.classList.remove('is-hidden');
    this.#lastY = y;
  }

  #openTrigger() {
    return (this.refs.triggers || []).find((el) => el.getAttribute('aria-expanded') === 'true');
  }

  #toggle(trigger) {
    if (trigger.getAttribute('aria-expanded') === 'true') this.#close(trigger);
    else this.#open(trigger);
  }

  #open(trigger) {
    const current = this.#openTrigger();
    if (current === trigger) return;
    if (current) this.#close(current, true);
    trigger.setAttribute('aria-expanded', 'true');
    document.getElementById(trigger.getAttribute('aria-controls'))?.removeAttribute('hidden');
  }

  #close(trigger, instant = false) {
    trigger.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    panel.toggleAttribute('data-instant', instant);
    panel.setAttribute('hidden', '');
  }
}

define('pg-header', PgHeader);

// ── Add-to-cart feedback ─────────────────────────────────────────────────────

document.addEventListener('pg:cart:changed', (event) => {
  const { action, response } = /** @type {CustomEvent} */ (event).detail;
  if (action !== 'add') return;

  // The header cart wrapper was just re-rendered; animate the fresh one.
  requestAnimationFrame(() => {
    for (const cart of document.querySelectorAll('.pg-header__cart')) {
      cart.classList.remove('is-dropping');
      void (/** @type {HTMLElement} */ (cart).offsetWidth);
      cart.classList.add('is-dropping');
      cart.addEventListener('animationend', () => cart.classList.remove('is-dropping'), { once: true });
    }
  });

  const item = response.items?.[0];
  if (item) {
    toast(t('added_to_cart', { title: item.product_title }), {
      icon: 'check-circle',
      onClick: () => openDialog('pg-cart-drawer'),
    });
  }
});
